"use client"

import { useState } from "react"
import { useForm } from "react-hook-form"
import { zodResolver } from "@hookform/resolvers/zod"
import * as z from "zod"
import { Loader2, Mail, Phone, User, MessageSquare, ArrowLeft } from "lucide-react"
import { toast } from "react-toastify"
import { BACKEND_URL } from "../utils/env.config"
import { useAppSelector } from "../redux/hooks"
import { useNavigate } from "react-router-dom"
import { DetectOSAndBrowser } from "../redux/api/DetectBroweser"

const contactSchema = z.object({
    fullName: z.string().min(2, "Full name must be at least 2 characters"),
    email: z.string().email("Please enter a valid email address"),
    phoneNumber: z.string().optional(),
    subject: z.string().min(5, "Subject must be at least 5 characters"),
    message: z.string().min(10, "Message must be at least 10 characters"),
})

type ContactFormData = z.infer<typeof contactSchema>

export function ContactForm() {
    const navigate = useNavigate()
    const [isSubmitting, setIsSubmitting] = useState(false)
    const user = useAppSelector(state => state.auth.user)

    const {
        register,
        handleSubmit,
        formState: { errors },
        reset,
    } = useForm<ContactFormData>({
        resolver: zodResolver(contactSchema),
        defaultValues: {
            email: user?.primaryEmail,
            fullName: `${user?.firstName || ""} ${user?.lastName || ""}`.trim(),
            phoneNumber: user?.primaryPhone,
        }
    })
    const onSubmit = async (data: ContactFormData) => {
        setIsSubmitting(true)

        try {
            const response = await fetch(`${BACKEND_URL}General/ContactUs`, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    "Client-OS": DetectOSAndBrowser().OS,
                    "Client-Browser": DetectOSAndBrowser().Browser,
                },
                body: JSON.stringify({
                    Email: data.email,
                    FullName: data.fullName,
                    PhoneNumber: data.phoneNumber,
                    Subject: data.subject,
                    Message: data.message,
                }),
            })

            if (response.ok) {
                toast.success("Message sent successfully! We'll get back to you as soon as possible.")
                navigate(user ? '/dashboard' : '/home')
                reset()
            } else {

                throw new Error("Failed to send message")
            }
        } catch (error) {
            toast.error("Error sending message. Please try again later or contact us directly.")
        } finally {
            setIsSubmitting(false)
        }
    }

    return (
        <div className="w-full max-w-3xl mx-auto">
            <div className="bg-white dark:bg-[#242424]/80 rounded-lg border border-gray-200 dark:border-gray-800 shadow-sm">
                <header className="bg-white dark:bg-[#212121] shadow-sm">
                    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
                        <div className="flex items-center justify-between">
                            <button
                                onClick={() => {
                                    navigate(-1)
                                }}
                                className="inline-flex items-center text-sm font-medium text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 transition-colors duration-200"
                            >
                                <ArrowLeft className="mr-2 h-4 w-4" />
                                Back
                            </button>
                        </div>
                    </div>
                </header>
                <div className="p-6 text-center border-b border-gray-200 dark:border-gray-800">
                    <h2 className="text-2xl font-bold bg-gradient-to-r from-accent-500 to-accent-600 bg-clip-text text-transparent">
                        Get in Touch
                    </h2>
                    <p className="text-base text-gray-600 dark:text-gray-400 mt-2">
                        We'd love to hear from you. Send us a message and we'll respond as soon as possible.
                    </p>
                </div>

                <div className="p-6">
                    <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div className="space-y-2">
                                <label htmlFor="fullName" className="text-xs font-medium text-gray-700 dark:text-gray-300">
                                    Full Name
                                </label>
                                <div className="relative">
                                    <User className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
                                    <input
                                        id="fullName"
                                        placeholder="Enter full name"
                                        className="w-full pl-10 pr-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-[#2a2a2a] text-gray-900 dark:text-gray-100 placeholder-gray-500 dark:placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-accent-500 focus:border-transparent"
                                        {...register("fullName")}
                                    />
                                </div>
                                {errors.fullName && <p className="text-xs text-red-500">{errors.fullName.message}</p>}
                            </div>

                            <div className="space-y-2">
                                <label htmlFor="email" className="text-xs font-medium text-gray-700 dark:text-gray-300">
                                    Email Address
                                </label>
                                <div className="relative">
                                    <Mail className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
                                    <input
                                        id="email"
                                        type="email"
                                        placeholder="Enter your email"
                                        className="w-full pl-10 pr-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-[#2a2a2a] text-gray-900 dark:text-gray-100 placeholder-gray-500 dark:placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-accent-500 focus:border-transparent"
                                        {...register("email")}
                                    />
                                </div>
                                {errors.email && <p className="text-xs text-red-500">{errors.email.message}</p>}
                            </div>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div className="space-y-2">
                                <label htmlFor="phoneNumber" className="text-xs font-medium text-gray-700 dark:text-gray-300">
                                    Phone Number
                                </label>
                                <div className="relative">
                                    <Phone className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
                                    <input
                                        id="phoneNumber"
                                        placeholder="Enter your phone number"
                                        className="w-full pl-10 pr-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-[#2a2a2a] text-gray-900 dark:text-gray-100 placeholder-gray-500 dark:placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-accent-500 focus:border-transparent"
                                        {...register("phoneNumber")}
                                    />
                                </div>
                                {errors.phoneNumber && <p className="text-xs text-red-500">{errors.phoneNumber.message}</p>}
                            </div>

                            <div className="space-y-2">
                                <label htmlFor="subject" className="text-xs font-medium text-gray-700 dark:text-gray-300">
                                    Subject
                                </label>
                                <div className="relative">
                                    <MessageSquare className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
                                    <input
                                        id="subject"
                                        placeholder="How can we help?"
                                        className="w-full pl-10 pr-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-[#2a2a2a] text-gray-900 dark:text-gray-100 placeholder-gray-500 dark:placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-accent-500 focus:border-transparent"
                                        {...register("subject")}
                                    />
                                </div>
                                {errors.subject && <p className="text-xs text-red-500">{errors.subject.message}</p>}
                            </div>
                        </div>

                        <div className="space-y-2">
                            <label htmlFor="message" className="text-xs font-medium text-gray-700 dark:text-gray-300">
                                Message
                            </label>
                            <textarea
                                id="message"
                                placeholder="Tell us more about your inquiry..."
                                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-[#2a2a2a] text-gray-900 dark:text-gray-100 placeholder-gray-500 dark:placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-accent-500 focus:border-transparent min-h-[120px] resize-none"
                                {...register("message")}
                            />
                            {errors.message && <p className="text-xs text-red-500">{errors.message.message}</p>}
                        </div>

                        <button
                            type="submit"
                            className="w-full bg-gradient-to-r from-accent-500 to-accent-600 hover:from-accent-600 hover:to-accent-600 text-white font-medium py-2 px-4 rounded-md transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                            disabled={isSubmitting}
                        >
                            {isSubmitting ? (
                                <>
                                    <Loader2 className="mr-2 h-4 w-4 animate-spin inline" />
                                    Sending Message...
                                </>
                            ) : (
                                "Send Message"
                            )}
                        </button>
                    </form>
                </div>
            </div>
        </div>
    )
}
